import { defineComponent, PropType } from "vue";

import { COMPONENT_NAME, SVG } from "./attributes";

import { UIButton } from "@/components/ui";
import {
  SharedCryptoSelect,
  SharedSelect,
  SharedCurrency,
} from "@/components/shared";
import { ApplicationFormScore } from "../form-score";
import { RateCalculationGuard } from "./rate-calculation-guard";

import { SharedCryptoSelectOption } from "@/shared/constants/constants";
import { ApplicationStoreRequest } from "@/shared/repository/modules/application/repo";
import { AdminCurrencyType } from "@/shared/repository/modules/admin/currency/enums";
import {
  WalletListResponse,
  WalletWithAmountResponse,
  WalletTradingPairResponse,
} from "@/shared/repository/modules/wallet/repo";
import { ApplicationCommissionRequestFactory } from "@/shared/repository/modules/application/factory";
import { ApplicationComponent } from "@/views/application/model";
import { RequisitesCryptoResponse } from "@/shared/repository/modules/requisites/repo";
import { SharedSelectOption } from "@/components/shared/select/model";
import { SelectOptionFactory } from "@/shared/factory/select";
import {
  formatFiatCurrency,
  formatCryptoCurrency,
} from "@/shared/utils/amount-helpers";

export default defineComponent({
  name: COMPONENT_NAME,
  components: {
    UIButton,
    SharedCryptoSelect,
    SharedCurrency,
    SharedSelect,
    ApplicationFormScore,
  },
  props: {
    requisites: {
      type: <PropType<RequisitesCryptoResponse[]>>Array,
      default: () => [],
    },
    component: {
      type: <PropType<ApplicationComponent>>String,
      default: ApplicationComponent.form,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isResult: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    "update:component": null,
    "update:isLoading": null,
    "update:isResult": null,
  },
  data() {
    return {
      rateGuard: new RateCalculationGuard(),
      userRepo: this.$projectServices.userRepo,
      walletRepo: this.$projectServices.walletRepo,
      walletRepository: this.$projectServices.walletRepository,
      applicationRepo: this.$projectServices.applicationRepo,
      applicationRepository: this.$projectServices.applicationRepository,
      timer: 0,
      pair: <WalletTradingPairResponse | undefined>undefined,
      isSwapAnimating: false,
      isRateLoading: false,
    };
  },
  beforeMount() {
    this.timer = setInterval(() => {
      this.handleCommission();
    }, 60 * 1000);
  },
  beforeUnmount() {
    clearInterval(this.timer);
    this.rateGuard.abort();
  },
  watch: {
    async "displayedApplicationStoreRequest.currency_id_from"() {
      await this.handleCommission();
    },

    async "displayedApplicationStoreRequest.currency_id_to"() {
      await this.handleCommission();
    },

    async "displayedApplicationStoreRequest.amount"() {
      await this.handleCommission();
    },
  },
  computed: {
    notVerified(): boolean {
      return !this.userRepo.isVerified;
    },
    displayedWalletList(): WalletListResponse[] {
      return this.walletRepo.list;
    },

    displayedWalletWithAmount(): WalletWithAmountResponse[] {
      return this.walletRepo.withAmount;
    },

    displayedApplicationStoreRequest(): ApplicationStoreRequest {
      return this.applicationRepo.application;
    },

    displayedCurrencyFrom():
      | WalletWithAmountResponse
      | WalletListResponse
      | undefined {
      const { currency_id_from } = this.displayedApplicationStoreRequest;

      return this.displayedWalletList.find(
        (currencyItem) => currencyItem.currency_id === currency_id_from
      );
    },

    displayedCurrencyTo():
      | WalletWithAmountResponse
      | WalletListResponse
      | undefined {
      const { currency_id_to } = this.displayedApplicationStoreRequest;

      return this.displayedWalletList.find(
        (currencyItem) => currencyItem.currency_id === currency_id_to
      );
    },

    displayedFromOptions(): SharedCryptoSelectOption[] {
      const list = this.walletRepo.list.map((currency) => {
        return {
          id: currency.currency_id,
          label: this.currencyAmountFormatter(currency) || "0",
          maxAmount: +currency.amount || 0,
          isActive:
            this.displayedApplicationStoreRequest.currency_id_from ===
            currency.currency_id,
          currency: {
            fiatIcon:
              currency.type === AdminCurrencyType.fiat && currency.currency
                ? currency.currency.country
                : currency.country,
            cryptoIcon:
              currency.type === AdminCurrencyType.crypto ? currency.name : "",
            name: currency.currency ? currency.currency.name : currency.name,
            type: currency.type,
          },
        };
      });
      return list;
    },

    displayedToOptions(): SharedCryptoSelectOption[] {
      return this.displayedWalletList
        .filter((currency) => {
          const { currency_id_from } = this.displayedApplicationStoreRequest;

          if (currency_id_from)
            return currency.currency_id !== currency_id_from;

          return currency;
        })
        .map((currency) => ({
          id: currency.currency_id,
          label: currency.amount ? this.currencyAmountFormatter(currency) : "",
          currency: {
            fiatIcon:
              currency.type === AdminCurrencyType.fiat && currency.currency
                ? currency.currency.country
                : currency.country,
            cryptoIcon:
              currency.type === AdminCurrencyType.crypto ? currency.name : "",
            name: currency.currency ? currency.currency.name : currency.name,
            type: currency.type,
          },
          maxAmount: 0,
          isActive:
            this.displayedApplicationStoreRequest.currency_id_to ===
            currency.currency_id,
        }));
    },

    displayedRequisitesFromOptions(): SharedSelectOption[] {
      return this.requisites.map((requisite) =>
        SelectOptionFactory(
          {
            id: requisite.id,
            label: requisite.name,
            value: requisite.id,
          },
          this.displayedApplicationStoreRequest.user_requisite_id_from!
        )
      );
    },

    displayedRequisitesToOptions(): SharedSelectOption[] {
      return this.requisites.map((requisite) =>
        SelectOptionFactory(
          {
            id: requisite.id,
            label: requisite.name,
            value: requisite.id,
          },
          this.displayedApplicationStoreRequest.user_requisite_id_to!
        )
      );
    },

    displayedArrowIcon(): string {
      return SVG.arrow;
    },

    displayedCourse(): string {
      const { currency_type_to, execution_price } =
        this.displayedApplicationStoreRequest;

      return this.currencyTypeAmountFormatter(
        currency_type_to as AdminCurrencyType,
        execution_price
      );
    },

    displayedCourseInverse(): string {
      const { currency_type_from, execution_price } =
        this.displayedApplicationStoreRequest;

      return this.currencyTypeAmountFormatter(
        currency_type_from as AdminCurrencyType,
        1 / execution_price
      );
    },

    displayedCommission(): string {
      const { currency_type_from, commissions } =
        this.displayedApplicationStoreRequest;

      return this.currencyTypeAmountFormatter(
        currency_type_from as AdminCurrencyType,
        commissions
      );
    },

    displayedAmount(): string {
      const { currency_type_to, amount, commissions, execution_price } =
        this.displayedApplicationStoreRequest;

      return this.currencyTypeAmountFormatter(
        currency_type_to as AdminCurrencyType,
        (amount - commissions) * execution_price
      );
    },

    displayedCurrencyName(): string {
      return this.displayedCurrencyFrom
        ? this.displayedCurrencyFrom.currency?.name ||
            (this.displayedCurrencyFrom as WalletListResponse).name
        : "";
    },

    isRequisitesFromShow(): boolean {
      return false;
    },

    isRequisitesToShow(): boolean {
      return false;
    },

    isCommissionsShow(): boolean {
      const { amount, commissions, execution_price } =
        this.displayedApplicationStoreRequest;

      return (amount - commissions) * execution_price > 0;
    },

    isNextDisabled(): boolean {
      const {
        currency_id_from,
        currency_id_to,
        currency_type_from,
        currency_type_to,
        amount,
        max_amount,
        execution_price,
        transaction_volume,
        commissions,
      } = this.displayedApplicationStoreRequest;

      const isDisabled =
        !currency_id_from ||
        !currency_id_to ||
        !currency_type_from ||
        !currency_type_to ||
        amount <= 0 ||
        amount > max_amount ||
        !execution_price ||
        transaction_volume <= 0 ||
        commissions < 0 ||
        this.isLoading;

      return isDisabled;
    },

    isErrorAmount(): boolean {
      if (
        this.displayedApplicationStoreRequest.currency_type_from ===
          AdminCurrencyType.fiat &&
        this.displayedApplicationStoreRequest.currency_type_to ===
          AdminCurrencyType.fiat
      ) {
        return (
          this.displayedApplicationStoreRequest.amount >
          this.displayedApplicationStoreRequest.max_amount
        );
      }
      if (
        this.displayedApplicationStoreRequest.currency_type_from ===
          AdminCurrencyType.crypto &&
        this.displayedApplicationStoreRequest.currency_type_to ===
          AdminCurrencyType.crypto
      ) {
        return (
          this.displayedApplicationStoreRequest.amount >
          this.displayedApplicationStoreRequest.max_amount
        );
      }
      return false;
    },
  },
  methods: {
    currencyAmountFormatter(currency: WalletListResponse): string {
      return currency.type === AdminCurrencyType.fiat
        ? formatFiatCurrency(currency.amount)
        : formatCryptoCurrency(currency.amount);
    },

    currencyTypeAmountFormatter(
      type: AdminCurrencyType,
      amount: number
    ): string {
      if (type === AdminCurrencyType.fiat) {
        return formatFiatCurrency(amount);
      } else if (type === AdminCurrencyType.crypto) {
        return formatCryptoCurrency(amount);
      }
      return String(amount);
    },

    async handleCurrencyIdFrom(currency_id_from: number): Promise<void> {
      try {
        this.$emit("update:isLoading", true);

        const option = this.displayedFromOptions.find(
          (option) => option.id === currency_id_from
        )!;

        this.applicationRepo.UPDATE_APPLICATION({
          ...this.displayedApplicationStoreRequest,
          currency_id_from,
          currency_type_from: option.currency.type,
          user_requisite_id_from: 0,
          max_amount: option.maxAmount,
        });
      } catch (error) {
        console.log(error);
      } finally {
        this.$emit("update:isLoading", false);
      }
    },

    async handleCurrencyIdTo(currency_id_to: number): Promise<void> {
      try {
        this.$emit("update:isLoading", true);

        const option = this.displayedToOptions.find(
          (option) => option.id === currency_id_to
        )!;

        this.applicationRepo.UPDATE_APPLICATION({
          ...this.displayedApplicationStoreRequest,
          currency_id_to,
          currency_type_to: option?.currency.type,
          user_requisite_id_to: 0,
        });
      } catch (error) {
        console.log(error);
      } finally {
        this.$emit("update:isLoading", false);
      }
    },

    handleAmount(amount: number): void {
      this.applicationRepo.UPDATE_APPLICATION({
        ...this.displayedApplicationStoreRequest,
        amount,
      });
    },

    handleSwap(): void {
      const {
        currency_id_from,
        currency_id_to,
        currency_type_from,
        currency_type_to,
        user_requisite_id_from,
        user_requisite_id_to,
      } = this.displayedApplicationStoreRequest;

      if (!currency_id_to) return;

      this.isSwapAnimating = false;
      window.requestAnimationFrame(() => {
        this.isSwapAnimating = true;
      });

      const newMaxAmount =
        this.displayedWalletList.find(
          (currency) => currency.currency_id === currency_id_to
        )?.amount ?? 0;

      this.applicationRepo.UPDATE_APPLICATION({
        ...this.displayedApplicationStoreRequest,
        currency_id_from: currency_id_to,
        currency_type_from: currency_type_to,
        user_requisite_id_from: user_requisite_id_to,
        max_amount: newMaxAmount,
        currency_id_to: currency_id_from,
        currency_type_to: currency_type_from,
        user_requisite_id_to: user_requisite_id_from,
        amount: 0,
      });
    },

    handleSwapAnimationEnd(): void {
      this.isSwapAnimating = false;
    },

    handleMax(): void {
      const { currency_id_from } = this.displayedApplicationStoreRequest;
      if (!currency_id_from) return;
      const maxAmount =
        this.displayedFromOptions.find(
          (option) => option.id === currency_id_from
        )?.maxAmount ?? 0;
      const application = {
        ...this.displayedApplicationStoreRequest,
        amount: maxAmount,
      };
      this.applicationRepo.UPDATE_APPLICATION(application);
    },

    handleMin(): void {
      if (!this.pair) return;
      const { currency_id_from } = this.displayedApplicationStoreRequest;

      this.applicationRepo.UPDATE_APPLICATION({
        ...this.displayedApplicationStoreRequest,
        amount: currency_id_from
          ? Number(
              this.pair.side === "sell"
                ? this.pair.min
                : this.pair.min * this.pair.rate
            )
          : 0,
      });
    },

    async getCryptoPair(): Promise<void> {
      try {
        this.$emit("update:isLoading", true);

        const {
          currency_id_from,
          currency_id_to,
          currency_type_from,
          currency_type_to,
        } = this.displayedApplicationStoreRequest;

        if (!currency_id_from || !currency_id_to) return;

        if (
          currency_type_from === AdminCurrencyType.crypto &&
          currency_type_to === AdminCurrencyType.crypto &&
          currency_id_from !== currency_id_to
        ) {
          this.pair = await this.walletRepository.tradingPairs({
            from: currency_id_from,
            to: currency_id_to,
          });
          this.applicationRepo.UPDATE_APPLICATION({
            ...this.displayedApplicationStoreRequest,
            pair: this.pair,
          });
        } else {
          this.pair = undefined;
          // this.applicationRepo.UPDATE_APPLICATION({
          //   ...this.displayedApplicationStoreRequest,
          //   amount: 0,
          // });
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.$emit("update:isLoading", false);
      }
    },

    async handleCommission(): Promise<void> {
      const { id: rateCalculationId, controller: abortController } =
        this.rateGuard.begin();

      const {
        currency_id_from,
        currency_id_to,
        currency_type_from,
        currency_type_to,
      } = this.displayedApplicationStoreRequest;

      try {
        if (
          !currency_id_from ||
          !currency_id_to ||
          !currency_type_from ||
          !currency_type_to
        ) {
          this.applicationRepo.UPDATE_APPLICATION({
            ...this.displayedApplicationStoreRequest,
            commissions: 0,
            execution_price: 0,
            transaction_volume: 0,
          });
          return;
        }

        this.isRateLoading = true;

        if (!this.pair) {
          await this.getCryptoPair();
        }

        if (!this.rateGuard.isCurrent(rateCalculationId)) return;

        this.applicationRepo.UPDATE_APPLICATION({
          ...this.displayedApplicationStoreRequest,
          commissions: 0,
          execution_price: 0,
          transaction_volume: 0,
        });

        const { amount } = this.displayedApplicationStoreRequest;

        if (
          !currency_id_from ||
          !currency_id_to ||
          !currency_type_from ||
          !currency_type_to
        )
          return;

        const { execute_price, transaction_volume, commission } =
          await this.applicationRepository.commission(
            ApplicationCommissionRequestFactory({
              currency_id_from,
              currency_id_to,
              currency_type_from: currency_type_from as AdminCurrencyType,
              currency_type_to: currency_type_to as AdminCurrencyType,
              amount,
            }),
            abortController,
            this.userRepo.userInfo.id
          );

        if (!this.rateGuard.isCurrent(rateCalculationId)) return;

        this.applicationRepo.UPDATE_APPLICATION({
          ...this.displayedApplicationStoreRequest,
          commissions: commission,
          execution_price: execute_price,
          transaction_volume,
        });

        this.$emit("update:isLoading", true);
      } catch (error) {
        if (!this.rateGuard.isCurrent(rateCalculationId)) return;

        this.applicationRepo.UPDATE_APPLICATION({
          ...this.displayedApplicationStoreRequest,
          commissions: 0,
          execution_price: 0,
          transaction_volume: 0,
        });
      } finally {
        if (this.rateGuard.isCurrent(rateCalculationId)) {
          this.isRateLoading = false;
          this.$emit("update:isLoading", false);
        }
      }
    },

    handleCryptoCreate(): void {
      this.$emit("update:component", ApplicationComponent.crypto);
    },

    async handleNext(): Promise<void> {
      if (!this.pair) {
        await this.getCryptoPair();
      }
      this.$emit("update:isResult", true);
    },
  },
  created() {
    const currency_id_from = this.displayedCurrencyFrom?.currency_id;

    if (currency_id_from) {
      try {
        const option = this.displayedFromOptions.find(
          (option) => option.id === currency_id_from
        )!;

        this.applicationRepo.UPDATE_APPLICATION({
          ...this.displayedApplicationStoreRequest,
          currency_id_from,
          currency_type_from: option.currency.type,
          user_requisite_id_from: 0,
          max_amount: option.maxAmount,
        });
      } catch (error) {
        console.log(error);
      }
    }
  },
});
